import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';

// Domain
import {
  partitionColumnFiltersIntoAdjacentUnits,
  flattenColumnFilterUnits,
  getSortableIdForColumnFilterUnit,
  reorderVisibleColumnFilterUnits,
} from '../../../domain/tableColumnManagement/tableColumnManagementUseCase';

// Components
import ListItem from '../../molecules/ListItem/ListItem';
import ColumnGroupListItem from '../../molecules/ColumnGroupListItem/ColumnGroupListItem';
import IconButton from '../../atoms/IconButton/IconButton';
import Picto from '../../atoms/Picto/Picto';

// Styles (same outer shell as DndListItem; inner list is column for stacked rows)
import dndStyles from '../../atoms/DndListItem/DndListItem.module.css';
import listStyles from './ColumnManagementDndList.module.css';

/**
 * Drag-and-drop list for column management: adjacent same groupId = one sortable row.
 * @param {object} props
 * @param {ApiManagerTableFiltersColumnFilters[]} props.columnFiltersLocal
 * @param {(columns: ApiManagerTableFiltersColumnFilters[]) => void} props.onSetColumnsOrder
 * @param {(id: string, pref: Partial<ApiManagerTableFiltersColumnFilters>) => void} props.onSetColumnPreference
 * @param {(cols: ApiManagerTableFiltersColumnFilters[], pref: Partial<ApiManagerTableFiltersColumnFilters>) => void} props.onSetColumnPreferenceGroup
 * @returns {JSX.Element}
 */
function ColumnManagementDndList({
  columnFiltersLocal,
  onSetColumnsOrder,
  onSetColumnPreference,
  onSetColumnPreferenceGroup,
}) {
  const { t } = useTranslation();

  const allUnits = useMemo(
    () => partitionColumnFiltersIntoAdjacentUnits(columnFiltersLocal),
    [columnFiltersLocal],
  );

  const visibleUnits = useMemo(
    () => allUnits.filter((u) => u.columns.some((c) => c.visible)),
    [allUnits],
  );

  const sortableIds = useMemo(
    () => visibleUnits.map((u) => getSortableIdForColumnFilterUnit(u)),
    [visibleUnits],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const requiredHideLabel = t('table.filters.requiredColumnCannotHide');

  /**
   * @param {import('@dnd-kit/core').DragEndEvent} event
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const newUnits = reorderVisibleColumnFilterUnits(
      allUnits,
      visibleUnits,
      oldIndex,
      newIndex,
    );
    onSetColumnsOrder(flattenColumnFilterUnits(newUnits));
  };

  return (
    <div className={dndStyles.container}>
      <div className={dndStyles['dnd-items']}>
        <div
          id="tableColumnsVisible_container"
          className={dndStyles['dnd-items-container']}
        >
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
              <div className={listStyles.list}>
                {visibleUnits.map((unit) => {
                  const sortableId = getSortableIdForColumnFilterUnit(unit);
                  const { columns } = unit;
                  const isGroup = columns.length > 1;
                  const anyRequired = columns.some((c) => c.required);
                  const allPinned = columns.every((c) => c.pinned);

                  const groupActions = (
                    <>
                      <IconButton
                        icon="fa-thumbtack"
                        color="transparent"
                        iconColor={allPinned ? 'primary' : ''}
                        size="medium"
                        onClick={() => onSetColumnPreferenceGroup(columns, {
                          pinned: !allPinned,
                        })}
                      />
                      {anyRequired ? (
                        <span
                          className={listStyles['hide-slot']}
                          title={requiredHideLabel}
                          aria-label={requiredHideLabel}
                        >
                          <Picto
                            icon="lock"
                            className={listStyles['required-lock']}
                          />
                        </span>
                      ) : (
                        <IconButton
                          icon="eye-slash"
                          color="transparent"
                          size="medium"
                          onClick={() => onSetColumnPreferenceGroup(columns, {
                            visible: false,
                          })}
                        />
                      )}
                    </>
                  );

                  if (isGroup) {
                    return (
                      <ColumnGroupListItem
                        key={sortableId}
                        id={sortableId}
                        columns={columns}
                      >
                        {groupActions}
                      </ColumnGroupListItem>
                    );
                  }

                  const [columnFilter] = columns;
                  return (
                    <ListItem
                      key={sortableId}
                      id={sortableId}
                      label={columnFilter.lib}
                      color={columnFilter.color}
                    >
                      <>
                        <IconButton
                          icon="fa-thumbtack"
                          color="transparent"
                          iconColor={columnFilter.pinned ? 'primary' : ''}
                          size="medium"
                          onClick={() => onSetColumnPreference(columnFilter.id, {
                            pinned: !columnFilter.pinned,
                          })}
                        />
                        {columnFilter.required ? (
                          <span
                            className={listStyles['hide-slot']}
                            title={requiredHideLabel}
                            aria-label={requiredHideLabel}
                          >
                            <Picto
                              icon="lock"
                              className={listStyles['required-lock']}
                            />
                          </span>
                        ) : (
                          <IconButton
                            icon="eye-slash"
                            color="transparent"
                            size="medium"
                            onClick={() => onSetColumnPreference(columnFilter.id, {
                              visible: false,
                            })}
                          />
                        )}
                      </>
                    </ListItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}

export default ColumnManagementDndList;
